import classNames from 'classnames'
import React from 'react'
import { Align, SwimmingPoolConfiguratorShapeFragment, SwimmingPoolConfiguratorShapeKind, SwimmingPoolConfiguratorVariantFragment, SwimmingPoolConfiguratorVariantProductFragment, UUID } from '../src/model/fragments'
import { getLocale, Locale } from '../src/model/locale'
import { getProductCategoryLink } from '../src/model/urls'
import { BarelyVisibleTitle } from './BarelyVisibleTitle'
import { Button, ButtonView } from './Button'
import { CardBox } from './CardBox'
import SvgExternalLink from './icons/SvgExternalLink'
import { Input } from './Input'
import { Picture, PictureFit } from './Picture'
import { ProductList, ProductListKind } from './ProductList'
import { Spacer } from './Spacer'
import { WidthBound } from './WidthBound'
import {calculateSwimmingPoolDetails, SwimmingPoolConfiguratorDTO, parseSwimmingPoolSpec, renderSwimmingPoolSvg, SwimmingPoolSchemaDimensionsType, SwimmingPoolSpecData, stringifySwimmingPoolSpec, checkVariantProductAvailability} from '../src/utils/swimmingPoolConfigurator'
import { PrintButton } from './PrintButton'
import { ShareLink } from './ShareLink'
import NextImage from 'next/image'	

const MIN_POOL_LENGTH = 1000;
const MAX_POOL_LENGTH = 25000;

enum SwimmingPoolConfiguratorState {
	shape = 1,
	variant = 2,
	dimensions = 3,
	final = 4
};

interface SwimmingPoolConfiguratorCommonProps
{
	locales: Locale[]
	show: boolean
}

/**************************************************************** */
/** Krok 1 - vyber typu / pudorysu bazenu *********************** */
/**************************************************************** */
interface SwimmingPoolConfiguratorStateShapeProps extends SwimmingPoolConfiguratorCommonProps
{
	swimmingPoolShapes: SwimmingPoolConfiguratorShapeFragment[]
	defaultVariant: SwimmingPoolConfiguratorVariantFragment,
	shape: SwimmingPoolConfiguratorShapeFragment | null
	select: (shape: SwimmingPoolConfiguratorShapeFragment, specData: SwimmingPoolSpecData[]) => void
}

const SwimmingPoolConfiguratorStateShape : React.FunctionComponent<SwimmingPoolConfiguratorStateShapeProps> = 
	({ swimmingPoolShapes, defaultVariant, shape, show, locales, select }) => {

	return (
		<>
			<div className={classNames('swimmingPoolConfiguratorLayout-state', 'swimmingPoolConfiguratorLayout-shape', show ? 'swimmingPoolConfiguratorLayout-show' : '')}>
				<div className={'swimmingPoolConfiguratorLayout-track'}>
					<span></span>
					{shape && 
						<a onClick={() => select(shape, parseSwimmingPoolSpec(shape.spec))}>
							<span>Další krok</span> <strong>›</strong>
						</a>
					}
					{
						!shape && <span></span>
					}
				</div>
				<Spacer initial={60} />
				<h2>Vyberte si prosím z předkonfigurovaných tvarů bazénů, konkrétní rozměry budete moci později upravit.</h2>
				<Spacer initial={20} />
				<div className="swimmingPoolConfiguratorLayout-shapeGrid">
					{ swimmingPoolShapes.map(sh => {
						const data = getLocale(sh.locales, locales)
						const specData = parseSwimmingPoolSpec(sh.spec)

						return data && <CardBox 
						
						isActive={ shape == sh }
						onClick={() => select(sh, specData)}
						title={ data.name }
						content={
							<>
							<div className="swimmingPoolConfiguratorLayout-svg">
								{
									
									React.useMemo(() => renderSwimmingPoolSvg(specData, defaultVariant, SwimmingPoolSchemaDimensionsType.none), [sh.spec])
								}
							</div>

							<p>{ data.perex }</p>
							</>
						}

						/>;
					})
						
					}
				</div>
				<Spacer initial={20} />
				<p>V našem portfoliu produktů nabízíme průběžné bazénové díly, obloukové díly R490 (R500), obloukové díly R1450 (R1500) a díly rohové.</p>
				<Spacer initial={100} />
			</div>
		</>
	);
};

/**************************************************************** */
/** Krok 2 - vyber produktove varianty ************************** */
/**************************************************************** */
interface SwimmingPoolConfiguratorStateVariantProps extends SwimmingPoolConfiguratorCommonProps
{
	swimmingPoolVariants: SwimmingPoolConfiguratorVariantFragment[]
	specData: SwimmingPoolSpecData[] | null
	variant: SwimmingPoolConfiguratorVariantFragment | null
	select: (shape: SwimmingPoolConfiguratorVariantFragment) => void
	back: () => void
}

const SwimmingPoolConfiguratorStateVariant : React.FunctionComponent<SwimmingPoolConfiguratorStateVariantProps> = 
	({ swimmingPoolVariants, specData, variant, show, select, locales, back }) => {

		const [selected, setSelected] = React.useState<SwimmingPoolConfiguratorVariantFragment | null>(variant ?? swimmingPoolVariants.find(v => v.isDefault) ?? swimmingPoolVariants[0]);


		// React.useEffect(() => {
		// 	swimmingPoolVariants.filter(v => v.image).forEach(v => {
		// 		new Image().src = v.image!.previewUrl; 
		// 	});
		// }, []);
		

		return (
			<>
				<div className={classNames('swimmingPoolConfiguratorLayout-state', 'swimmingPoolConfiguratorLayout-variant', show ? 'swimmingPoolConfiguratorLayout-show' : '')}>
					<div className={'swimmingPoolConfiguratorLayout-track'}>
						<a onClick={() => back()}><strong>‹</strong> <span>Předchozí krok</span></a>
						{variant && 
							<a onClick={() => select(variant)}><span>Další krok</span> <strong>›</strong></a>
						}
						{
							!variant && <span></span>
						}
					</div>
					<Spacer initial={60} />
					<h2>Vyberte si z našich produktových řad.</h2>

					<Spacer initial={20} />
					<div className={'swimmingPoolConfigurator-variantFlex'}>
						<div className="swimmingPoolConfiguratorLayout-variantGrid">
							{ specData && swimmingPoolVariants.filter(v => checkVariantProductAvailability(specData, v) ).map(v => {
								const data = getLocale(v.locales, locales)
								
								return data && <CardBox 
								
								isActive={ selected == v }
								onClick={() => setSelected(v)}
								title={ data.name }
								content={
									<>
										<p>{ data.perex }</p>
									</>
								}
		
								/>;
							})
								
							}
						</div>
					

						{ selected && 
							<>
								{ selected.image && 
									<div className={classNames('swimmingPoolConfiguratorLayout-variantPicture')}>
										<Picture
											image={selected.image}
											width={650}
											height={650}
											mode={'cover'}
											alt=""
										/>
									</div>
								}

								<div className={classNames('swimmingPoolConfiguratorLayout-variantButtons')}>

									{ selected.productType && 
										<Button icon={<SvgExternalLink />} view={ButtonView.outline}  linkProps={ {...getProductCategoryLink(locales[0], selected.productType.id, selected.productType.link.url ), replace: false } }>
											Přejít na produkty řady
										</Button>
									}

									<Button arrow={true} onClick={() => select(selected)}>
										Vybrat řadu bazénových lemů
									</Button>
								</div>
								
							</>
						}

							

					</div>
					<Spacer initial={100} />
				</div>
			</>
		);
};

/**************************************************************** */
/** Krok 3 - volba velikosti **************** */
/**************************************************************** */
interface SwimmingPoolConfiguratorStateDimensionsProps extends SwimmingPoolConfiguratorCommonProps
{
	variant: SwimmingPoolConfiguratorVariantFragment | null
	specData: SwimmingPoolSpecData[]
	setSpecData: (specData: SwimmingPoolSpecData[]) => void
	back: () => void
	next: () => void
}

const SwimmingPoolConfiguratorStateDimensions : React.FunctionComponent<SwimmingPoolConfiguratorStateDimensionsProps> = 
	({ variant, specData, setSpecData, show, back, next, locales }) => {

	if(!specData || !variant)
	{
		return <></>
	}

	const variantData = getLocale(variant.locales, locales);
	if(!variantData)
	{
		return <></>
	}

	const [inputSpecData, setInputSpecData] = React.useState<SwimmingPoolSpecData[]>(specData);
	const [hasError, setHasError] = React.useState<boolean>(false);
	React.useEffect(() => {
		setInputSpecData(specData);
	}, [show]);

	let j = 0
	return <>
		<div className={classNames('swimmingPoolConfiguratorLayout-state', 'swimmingPoolConfiguratorLayout-dimensions', show ? 'swimmingPoolConfiguratorLayout-show' : '')}>
			<div className={'swimmingPoolConfiguratorLayout-track'}>
				<a onClick={() => back()}><strong>‹</strong> <span>Předchozí krok</span></a>
				{!hasError && <a onClick={() => next()}><span>Další krok</span> <strong>›</strong></a>}
			</div>
			<Spacer initial={60} />
			<h2>Přizpůsobte si tvar bazénu svým potřebám.</h2>
			<BarelyVisibleTitle level={3} align={Align.left} >ŘADA { variantData.name }</BarelyVisibleTitle>

			<Spacer initial={20} />
			<div className={classNames('swimmingPoolConfiguratorLayout-dimensionsFlex')}>

				<div className="swimmingPoolConfiguratorLayout-dimensionsInputs">
				<Spacer initial={30} />
				{
					inputSpecData.map((d, i) => {

						if(!d.editable)
						{
							return <></>
						}

						return <>
						
							<Input
								label={'Rozměr ' + String.fromCharCode('A'.charCodeAt(0) + (j++)) + ' [mm]'}
								value={inputSpecData[i].length}
								min={inputSpecData[i].subtractStart + inputSpecData[i].subtractEnd + MIN_POOL_LENGTH}
								max={MAX_POOL_LENGTH}
								
								type={'number'}
								onChange={(elem) => {
									const value = parseFloat(elem.currentTarget.value);
									const min = parseFloat(elem.currentTarget.getAttribute('min')!);
									const max = parseFloat(elem.currentTarget.getAttribute('max')!);
									const error = isNaN(value) || value < min || value > max;

									if(error)
									{
										elem.currentTarget.className = elem.currentTarget.className.replace('error', '') + ' error';
										setHasError(true);
									}
									else 
									{
										elem.currentTarget.className = elem.currentTarget.className.replace('error', '').trim();
										setHasError(false);
									}

									const data = specData.map((dd, ii) => {

										if(ii === i || dd.ref === i)
										{
											return {...dd, length: value}
										}

										return dd
									})

									if(error)
									{
										setInputSpecData(data);
									}
									else 
									{
										setSpecData(data);
										setInputSpecData(data);
									}
								}}
							/>

						</>
					})
				}

				<p>
					Zobrazené kóty odpovídají vzdálenostem vnitřních hran bazénových lemů, bez započtení případného přesahu lemu do vnitřku bazénu (do 20mm). Ilustrační profil bazénového lemu je na obrázku níže. Hodnoty jsou v <strong>milimetrech</strong>.
					<br></br><br></br>
					<br></br>
					Pro získání vnějších rozměrů bazénu (včetně lemu) připočtěte na každé straně níže uvedenou rozdílovou šířku. Ta vzniká odečtením vnitřního přesahu lemu od celkové šířky průběžného lemovacího prvku vybrané řady.<br></br><br></br>
					Rozdílová šířka: <strong>{variant.products.find(p => p.kind == SwimmingPoolConfiguratorShapeKind.middle)!.thickness - variant.products.find(p => p.kind == SwimmingPoolConfiguratorShapeKind.middle)!.overlap}mm</strong><br></br><br></br>
					Při pokládce je nutno počítat mezi jednotlivými prvky se spárou o šířce 5-10mm. Tato spára je již v zobrazeném schématu zahrnuta.
				</p>

				<NextImage objectPosition="left center" objectFit="contain" src="/swimming_pool_illustration.png" alt="Náhled na tvar lemu" width="450" height="450" />
				

				</div>

				<div className="swimmingPoolConfiguratorLayout-svg">
					{React.useMemo(() => renderSwimmingPoolSvg(specData, variant, SwimmingPoolSchemaDimensionsType.letter), [specData, variant])}

					
				</div>
				

			</div>
			
			{/* <Spacer initial={60} /> */}
			<div className={classNames('swimmingPoolConfiguratorLayout-dimensionsButtons')}>
				<Button arrow={true} disabled={hasError} onClick={() => hasError ? '' : next()}>
					Pokračovat s vybranými rozměry
				</Button>
			</div>
			<Spacer initial={100} />
		</div>
	</>
};


/**************************************************************** */
/** Krok 4 - zobrazeni vysledku**************** */
/**************************************************************** */
interface SwimmingPoolConfiguratorStateFinalProps extends SwimmingPoolConfiguratorCommonProps
{
	shape: SwimmingPoolConfiguratorShapeFragment | null
	variant: SwimmingPoolConfiguratorVariantFragment | null
	specData: SwimmingPoolSpecData[]
	back: () => void
}

const SwimmingPoolConfiguratorStateFinal : React.FunctionComponent<SwimmingPoolConfiguratorStateFinalProps> = 
	({ variant, shape, specData, show, back, locales }) => {

	if(!specData || !variant || !shape)
	{
		return <></>
	}

	const variantData = getLocale(variant.locales, locales);
	if(!variantData)
	{
		return <></>
	}

	const shapeData = getLocale(shape.locales, locales);
	if(!shapeData)
	{
		return <></>
	}

	const details = calculateSwimmingPoolDetails(specData, variant);

	const link = 
		window.location.toString().replace(/\?.*$/g, '') + '?share=' + 
		encodeURIComponent(Buffer.from(JSON.stringify({
			shape: shape.id,
			variant: variant.id,
			spec: stringifySwimmingPoolSpec(specData)
		} as SwimmingPoolConfiguratorDTO), 'utf8').toString('base64'));

	// Velikost, tu zaokrouhlujeme nahoru
	// Tabulka s poctem ks a button pro vlozeni
	// Ilustracni obrazek
	// Okotovane schema
	return <>
		<div className={classNames('swimmingPoolConfiguratorLayout-state', 'swimmingPoolConfiguratorLayout-final', show ? 'swimmingPoolConfiguratorLayout-show' : '')}>
			<div className={'swimmingPoolConfiguratorLayout-track'}>
				<a onClick={() => back()}><strong>‹</strong> <span>Předchozí krok</span></a>
				<span></span>
			</div>
			<Spacer initial={60} />
			
			<div className={classNames('swimmingPoolConfiguratorLayout-finalFlex')}>
				<div>

					<div>
						<h2>Vámi nakonfigurovaný bazén PRESBETON</h2>
						<BarelyVisibleTitle level={3} align={Align.left} >ŘADA { variantData.name }</BarelyVisibleTitle>
					</div>
					
					<div>
						<p>{ variantData.perex }</p>

						<p>
							<strong>Tvar:</strong> {shapeData.name}<br /><br />

							<strong>Vnitřní obvod bazénu:</strong> {Math.round(details.circumference)}mm<br />
							<strong>Vnitřní šířka bazénu:</strong> {details.innerWidth}mm<br />
							<strong>Vnitřní délka bazénu:</strong> {(details.innerLength)}mm<br />
							Vnitřní rozměry odpovídají stěně bazénu.<br/><br />

							<strong>Vnější šířka bazénu:</strong> {details.outerWidth}mm<br />
							<strong>Vnější délka bazénu:</strong> {(details.outerLength)}mm<br />
							Vnější rozměry jsou včetně lemu až po jeho vnější okraj.
						</p>
					</div>

					<div>
						<p>
							<span>Pokládkové schéma je vykresleno níže na stránce.</span>
						</p>
					</div>
					
					

				</div>
				<div>
					{ variant.image && 
						<div className={classNames('swimmingPoolConfiguratorLayout-finalPicture')}>
							<Picture
								image={variant.image}
								width={650}
								height={650}
								mode={'cover'}
								alt={variantData.name}
							/>
						</div>
					}
				</div>
			</div>

			<Spacer initial={50} />
			<div className={classNames('swimmingPoolConfiguratorLayout-finalFlexShare')}>
				<PrintButton>Tisk / Export PDF</PrintButton>
				{/* <a className="shareShoppingListButton">
					<span className="shareShoppingListButton-icon">
						<SvgShare />
					</span>
					Sdílet bazén
				</a> */}

				<ShareLink link={link} />
			</div>
			
			<Spacer initial={50} />
			<div>
				<ProductList
					kind={ProductListKind.Generic}
					hasControlledQuantity={true}
					items={ 
						variant.products
						.filter((p, index) => variant.products.findIndex(pp => pp.product.id == p.product.id) == index)
						.filter(p => details.productCounts[p.product.id] !== undefined)
						.map(p => ({
							...p.product,
							controlledQuantity: details.productCounts[p.product.id] + details.reserveCounts[p.product.id]
						}))
					}
				/>
				<p>
					K potřebným prvkům jsme Vám pro jistotu přidali nějaké navíc. Prvky vyhotovené z odlišných šarží se mohou mírně lišit svojí povrchovou úpravou, a proto je lepší nakoupit s rezervou.<br />
					U bazénových řad s oboustrannými průběžnými prvky nemusí být k dispozici adekvátní rohové prvky. V takovém případě je nutné rohový prvek vyhotovit řezem dvou průběžných prvků.
				</p>
			</div>
			
			<Spacer initial={100} />
			<div className="swimmingPoolConfiguratorLayout-svg">
				
				{renderSwimmingPoolSvg(specData, variant, SwimmingPoolSchemaDimensionsType.number)}

				<table className="swimmingPoolConfiguratorLayout-svgTable">
					<tbody>
						<tr>
							<th>Schéma pokládky nakonfigurovaného bazénu</th>
							<td>PRESBETON</td>
						</tr>
						<tr>
							<td>{ shapeData.name }</td>
							<td>Kóty v mm</td>
						</tr>
					</tbody>
				</table>
			</div>
			<p className="swimmingPoolConfiguratorLayout-mobile">Pro lepší viditelnost schématu na mobilních zařízeních prosím zvolte možnost Tisk / Export PDF výše.</p>

			
			<Spacer initial={100} />
		</div>
	</>
};

export interface SwimmingPoolConfiguratorLayoutProps {
	swimmingPoolShapes: SwimmingPoolConfiguratorShapeFragment[]
	swimmingPoolVariants: SwimmingPoolConfiguratorVariantFragment[]
	locales: Locale[]
}

export const SwimmingPoolConfiguratorLayout: React.FunctionComponent<SwimmingPoolConfiguratorLayoutProps> =
	({ swimmingPoolShapes, swimmingPoolVariants, locales }) => {

		const [state, setState] = React.useState<SwimmingPoolConfiguratorState>(SwimmingPoolConfiguratorState.shape);
		const [shape, setShape] = React.useState<SwimmingPoolConfiguratorShapeFragment | null>(null);
		const [variant, setVariant] = React.useState<SwimmingPoolConfiguratorVariantFragment | null>(null);
		const [currentSpecData, setCurrentSpecData] = React.useState<SwimmingPoolSpecData[] | null>(null);

		React.useEffect(() => {

			const param = Object.fromEntries(window.location.search.replace('?', '').split('&').map(str => str.split('=')));
			if(param['share'] !== undefined)
			{
				const buffer = Buffer.from(decodeURIComponent(param['share']), 'base64');
				const json = JSON.parse(buffer.toString('utf8'));
				if(json['shape'] && json['variant'] && json['spec'])
				{
					const dto = json as SwimmingPoolConfiguratorDTO;
					const shape = swimmingPoolShapes.find(s => s.id === dto.shape);
					const variant = swimmingPoolVariants.find(v => v.id === dto.variant);
					try 
					{
						const spec = parseSwimmingPoolSpec(dto.spec);
						console.log('LOADING SHARED PRESET');
						console.log(shape);
						console.log(variant);
						console.log(spec);

						if(shape && variant)
						{
							setShape(shape);
							setVariant(variant);
							setCurrentSpecData(spec);
							setState(SwimmingPoolConfiguratorState.final);
						}
					}
					catch(e){ console.error(e); }
				}
			}

		}, []);

		return (
			

				<div className="swimmingPoolConfiguratorLayout">
					<WidthBound size="normal">
						<div className="swimmingPoolConfiguratorLayout-header">

							<div className={classNames("swimmingPoolConfiguratorLayout-headerstate", 'active')}>
								<strong>1</strong>
								<span>Výběr tvaru<br/>bazénu</span>
							</div>
							<div className={classNames("swimmingPoolConfiguratorLayout-headerstatearrow", 'active')}>
							›
							</div>
							<div className={classNames("swimmingPoolConfiguratorLayout-headerstate", state != SwimmingPoolConfiguratorState.shape ? 'active' : '')}>
								<strong>2</strong>
								<span>Výběr <br/>produktové řady</span>
							</div>
							<div className={classNames("swimmingPoolConfiguratorLayout-headerstatearrow", 'active')}>
							›
							</div>
							<div className={classNames("swimmingPoolConfiguratorLayout-headerstate", [SwimmingPoolConfiguratorState.final, SwimmingPoolConfiguratorState.dimensions].includes(state) ? 'active' : '')}>
								<strong>3</strong>
								<span>Úprava <br/>rozměrů</span>
							</div>
							<div className={classNames("swimmingPoolConfiguratorLayout-headerstatearrow", 'active')}>
							›
							</div>
							<div className={classNames("swimmingPoolConfiguratorLayout-headerstate", state == SwimmingPoolConfiguratorState.final ? 'active' : '')}>
								<strong>4</strong>
								<span>Schémata<br/>produkty a vizualizace</span>
							</div>
							<div className={classNames("swimmingPoolConfiguratorLayout-headerstatearrow", 'active')}>
							›
							</div>
						</div>
					</WidthBound>
					

					<WidthBound size="wide">
						<div className="swimmingPoolConfiguratorLayout-inner">
							<SwimmingPoolConfiguratorStateShape
								show={state === SwimmingPoolConfiguratorState.shape}
								defaultVariant={swimmingPoolVariants.find(v => v.isDefault) ?? swimmingPoolVariants[0]}
								swimmingPoolShapes={swimmingPoolShapes}
								shape={shape}
								locales={locales}
								select={(sh: SwimmingPoolConfiguratorShapeFragment, specData: SwimmingPoolSpecData[]) => {
									setShape(sh); 
									setState(SwimmingPoolConfiguratorState.variant); 
									setCurrentSpecData(specData);
								}}
							/>

							<SwimmingPoolConfiguratorStateVariant 
								show={state === SwimmingPoolConfiguratorState.variant}
								specData={currentSpecData}
								swimmingPoolVariants={swimmingPoolVariants}
								back={() => setState(SwimmingPoolConfiguratorState.shape)}
								variant={variant}
								select={(v: SwimmingPoolConfiguratorVariantFragment) => {setVariant(v); setState(SwimmingPoolConfiguratorState.dimensions); }}
								locales={locales}
							/>

							<SwimmingPoolConfiguratorStateDimensions 
								show={state === SwimmingPoolConfiguratorState.dimensions}
								back={() => setState(SwimmingPoolConfiguratorState.variant)}
								next={() => setState(SwimmingPoolConfiguratorState.final)}
								variant={variant}
								specData={currentSpecData!}
								setSpecData={setCurrentSpecData}
								locales={locales}
							/>

							<SwimmingPoolConfiguratorStateFinal
								show={state === SwimmingPoolConfiguratorState.final}
								back={() => setState(SwimmingPoolConfiguratorState.dimensions)}
								shape={shape}
								variant={variant}
								specData={currentSpecData!}
								locales={locales}
							/>
						</div>
					</WidthBound>
					<Spacer initial={100} />

				</div>
			
		);
	}

